import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// SearchInputComponent is not used in this component
import {
  Container,
  ContentWrapper,
  ReturnButton,
  SubmitButton as OriginalSubmitButton,
  OptionsContainer,
  LoadingOverlay,
  ChatBubbleContainer,
  ChatText,
  LogoImage,
  LogoBubbleWrapper,
  OptionIcon, // Re-using OptionIcon style
  OptionButton // Re-using OptionButton style
} from './styles'; // Assuming these styles are in a shared ./styles.js or similar

import compassImage from '../../assets/meo_write.png';
import ProgressBar from '../ProgressBar';
import useDeviceType from '../useDeviceType';
import apiCall from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import theme from '../../theme';
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png';
import { parseFormacodeData } from '../../utils/formaCodeUtils';
import { domainIconsFormacodesSousGrandDomaines } from './domainIconMap';
import defaultDomainIcon from '../../assets/point-dinterrogation-dessiner.png';

const OptionsScrollContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(var(--vh, 1vh) * 60); // Adjusted height
  overflow-y: auto;
  padding-right: 5px;
  
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.mediumGray} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.mediumGray};
    border-radius: 3px;
  }
`;

const StyledSubmitButton = styled(OriginalSubmitButton)`
  // Inherits styles from OriginalSubmitButton
  // Add any specific overrides if needed
`;

const DomainSelectionPrincipaux = () => {
  const navigate = useNavigate();
  const url = window.location.href;
  const modifyProfile = url.includes('profile/modify');
  const { isMobile } = useDeviceType();
  const { checkAuth } = useAuth();

  const [selectedFavoriteDomains, setSelectedFavoriteDomains] = useState([]); // Stores specific formacode.codes
  const [isLoading, setIsLoading] = useState(true); // For initial data load AND submission
  const [isTyping, setIsTyping] = useState(true);
  const [text, setText] = useState('');
  const fullText = "Quels sont les domaines que tu veux étudier en priorité ?";
  
  const [submitActive, setSubmitActive] = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  
  const [formacodeData, setFormacodeData] = useState({ formacodes: [], hierarchy: [] });
  const [dataLoadingError, setDataLoadingError] = useState(null);

  const [displayableDomains, setDisplayableDomains] = useState([]); // Stores full formacode objects to display
  const [hierarchyCodeToIconMap, setHierarchyCodeToIconMap] = useState(new Map());

  const MAX_FAVORITES = 50;

  // 1. Load Formacode CSV data
  useEffect(() => {
    const loadFormacodeData = async () => {
      setIsLoading(true); // Start global loading
      try {
        const response = await fetch('/formacodes_recoded_optimized.csv');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const csvData = await response.text();
        if (!csvData || csvData.trim() === "") throw new Error("CSV vide");
        
        const parsed = parseFormacodeData(csvData);
        setFormacodeData(parsed);
        setDataLoadingError(null);
      } catch (error) {
        console.error('Erreur chargement formacode:', error);
        setDataLoadingError(error.message);
        setFormacodeData({ formacodes: [], hierarchy: [] });
        // setIsLoading(false) will be handled by the effect consuming this data in case of error
      }
      // setIsLoading(false) is NOT set here; handled by the effect preparing displayableDomains
    };
    loadFormacodeData();
  }, []); // Run once on mount

  // 2. Prepare hierarchyCodeToIconMap (needed for icon resolution)
  useEffect(() => {
    if (formacodeData.formacodes.length > 0 && Object.keys(domainIconsFormacodesSousGrandDomaines).length > 0) {
      const newHierarchyMap = new Map();
      Object.entries(domainIconsFormacodesSousGrandDomaines).forEach(([domainName, icon]) => {
        const formacodeEntry = formacodeData.formacodes.find(
          fc => fc.descriptorRich === domainName && 
                fc.hierarchyCode && 
                fc.hierarchyCode.length === 4
        );
        if (formacodeEntry) {
          newHierarchyMap.set(formacodeEntry.hierarchyCode, icon);
        }
      });
      setHierarchyCodeToIconMap(newHierarchyMap);
    } else if (formacodeData.formacodes.length > 0) {
        console.warn("Formacodes chargés, mais domainIconsFormacodesSousGrandDomaines est vide ou non disponible.");
        setHierarchyCodeToIconMap(new Map());
    }
  }, [formacodeData]);

  // 3. Load previously selected HIERARCHY CODES and prepare corresponding FORMACODES for display
  useEffect(() => {
    if (dataLoadingError) {
      setIsLoading(false); // CSV loading error, finish global loading.
      setDisplayableDomains([]);
      return;
    }

    // Proceed only if formacodes are loaded.
    // hierarchyCodeToIconMap might still be forming (affects icons only).
    if (formacodeData.formacodes.length > 0) {
      let previouslySelectedHierarchyCodes = [];
      let storedHierarchyCodes = []; // Default empty array for hierarchy codes

      try {
        if( modifyProfile ) {
          // For profile modification, use 'domaine_formation' for specific formacode.codes
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          previouslySelectedHierarchyCodes = userData.sub_domaine_formation || [];
        } else {
          storedHierarchyCodes = localStorage.getItem('sub_domaine_formation');
          previouslySelectedHierarchyCodes = JSON.parse(storedHierarchyCodes);
        }
       
        if (Array.isArray(previouslySelectedHierarchyCodes)) {
          previouslySelectedHierarchyCodes = previouslySelectedHierarchyCodes.map(String);
        } else {
          previouslySelectedHierarchyCodes = []; // Malformed data
        }
        
        console.log("Previously selected hierarchy codes:", previouslySelectedHierarchyCodes);
      } catch (e) {
        console.error("Erreur parsing sub_domaine_formation (hierarchy codes) from localStorage:", e);
        previouslySelectedHierarchyCodes = [];
      }

      if (previouslySelectedHierarchyCodes.length > 0) {
        const domainsToDisplay = formacodeData.formacodes
          .filter(fc =>
            fc.hierarchyCode &&
            fc.hierarchyCode.length >= 4 &&
            previouslySelectedHierarchyCodes.includes(fc.hierarchyCode)
          )
          .map(formacode => ({
            ...formacode, // Keep the full formacode object (with its specific code)
            icon: getIconForDomain(formacode) // Resolve icon using its parent hierarchy code
          }));
        
        // Sort for consistent display
        domainsToDisplay.sort((a, b) => a.descriptorRich.localeCompare(b.descriptorRich));
        setDisplayableDomains(domainsToDisplay);

        // Auto-redirect if only one domain is available
        if (domainsToDisplay.length === 1 && !modifyProfile) {
          const singleDomain = domainsToDisplay[0];
          const favoritesToSave = [singleDomain.hierarchyCode || singleDomain.code];
          localStorage.setItem('domaines_principaux', JSON.stringify(favoritesToSave));
          navigate('/lycee'); // Or next step
          return;
        }
      } else {
        setDisplayableDomains([]); // No hierarchy codes selected previously
      }
      setIsLoading(false); // Data ready for display (or to show "no domains").
    } else if (!isLoading && formacodeData.formacodes.length === 0 && !dataLoadingError) {
      // Handles case where formacodeData is empty (e.g., empty CSV) but no explicit loading error.
      // This ensures isLoading becomes false if loadFormacodeData finishes with an empty dataset.
      setDisplayableDomains([]);
      setIsLoading(false);
    }
    // Dependencies: formacodeData for raw data, hierarchyCodeToIconMap for icons, dataLoadingError for error handling.
  }, [formacodeData, hierarchyCodeToIconMap, dataLoadingError, modifyProfile, navigate]);


  // Chat bubble typing effect
  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 15);
    return () => clearInterval(typingInterval);
  }, [fullText]);

  // getIconForDomain uses the formacode's hierarchyCode (first 4 chars) to find icon in map
  const getIconForDomain = (formacode) => {
    if (!formacode || !formacode.hierarchyCode) return defaultDomainIcon;
    const parentHierarchyCode = formacode.hierarchyCode.substring(0, 4);
    const icon = hierarchyCodeToIconMap.get(parentHierarchyCode);
    return icon || defaultDomainIcon;
  };

  // toggleFavoriteDomainSelection works with specific formacode.code
  const toggleFavoriteDomainSelection = (domainCode) => {
    setSelectedFavoriteDomains((prev) => {
      if (prev.includes(domainCode)) {
        return prev.filter(item => item !== domainCode);
      } else {
        if (prev.length < MAX_FAVORITES) {
            return [...prev, domainCode];
        }
        return prev; // Max reached, don't add
      }
    });
  };

  const handleSubmit = async () => {
    if (isTyping) return; // Only prevent if typing
    
    // Validation: au moins 1 domaine doit être sélectionné
    if (selectedFavoriteDomains.length === 0) {
      return; // Ne pas soumettre si aucun domaine n'est sélectionné
    }

    setIsLoading(true); // Indicate submission loading
    try {
      const favoritesToSave = selectedFavoriteDomains.map(domainCode => {
          const formacodeEntry = formacodeData.formacodes.find(fc => fc.code === domainCode);
          // Return the hierarchy code if available, otherwise fallback to the original code
          return formacodeEntry?.hierarchyCode || domainCode;
        }); // Contains specific formacode.codes

      if (modifyProfile) {
        await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', {
          domaine_formation: favoritesToSave // Send specific favorite codes
        });
        let userData = JSON.parse(localStorage.getItem('user'));
        userData.domaine_formation = favoritesToSave;
        localStorage.setItem('user', JSON.stringify(userData));
        await checkAuth();
        navigate('/profile');
      } else {
        // 'domaines_principaux' stores the specific favorite formacode.codes
        localStorage.setItem('domaines_principaux', JSON.stringify(favoritesToSave));
        navigate('/lycee'); // Or next step
      }
    } catch (error) {
      console.error('Error saving favorite domains:', error);
      // TODO: Show error to user
    } finally {
      setIsLoading(false); // Finish submission loading
    }
  };

  // Navigate back to the previous domain selection step
  const handleBack = () => navigate(modifyProfile ? '/profile/modify/domaines-favoris' : "/domaines-favoris");

  const handleSubmitPressStart = () => {
    if (isLoading || isTyping || selectedFavoriteDomains.length === 0) return;
    setSubmitActive(true);
  };

  const handleSubmitPressEnd = () => {
    if (isLoading || isTyping || selectedFavoriteDomains.length === 0) return;
    setSubmitActive(false);
    setTimeout(handleSubmit, 150);
  };
  
  const handleOptionPressStart = (code) => setActiveOption(code);
  const handleOptionPressEnd = () => setActiveOption(null);

  if (dataLoadingError && isLoading) { // Ensure isLoading is false if dataLoadingError is shown
    // This case should be rare if isLoading is set to false correctly in the useEffect
    // but as a safeguard:
    return (
        <Container>
            <ContentWrapper>
                <p style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
                    Erreur: {dataLoadingError}. Veuillez réessayer.
                </p>
                <ReturnButton onClick={handleBack} style={{ margin: '20px auto', display: 'block' }}>
                    Retour
                </ReturnButton>
            </ContentWrapper>
        </Container>
    );
  }

  const canSelectMoreFavorites = selectedFavoriteDomains.length < MAX_FAVORITES;
  const isSubmitDisabled = isTyping || (isLoading && submitActive) || selectedFavoriteDomains.length === 0;

  return (
    <Container>
      {/* Global loading overlay, shown during initial data fetch or submission */}
      {isLoading && <LoadingOverlay isLoading={isLoading} />} 
      <ReturnButton onClick={handleBack}>
        <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px' }} />
      </ReturnButton>
      <ContentWrapper>
        {!modifyProfile && <ProgressBar startPercent={60} endPercent={70} />}
        <LogoBubbleWrapper>
          <LogoImage src={compassImage} alt="Compass Logo" />
          <ChatBubbleContainer isMobile={isMobile}>
            <ChatText isVisible={text.length > 0}><span>{text}</span></ChatText>
          </ChatBubbleContainer>
        </LogoBubbleWrapper>

        <OptionsScrollContainer isMobile={isMobile}>
          {/* 
            The isLoading check here is critical.
            If global isLoading is true (due to initial fetch), show "Chargement...".
            Once global isLoading is false, then check displayableDomains.
          */}
          {isLoading ? ( // This refers to the global isLoading state
             <div style={{ textAlign: 'center', padding: '20px', color: theme.colors.textLighter }}>Chargement des domaines...</div>
          ) : dataLoadingError ? ( // If there was an error after loading attempt
            <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
              Erreur: {dataLoadingError}. Veuillez réessayer.
              <ReturnButton onClick={handleBack} style={{ margin: '20px auto', display: 'block' }}>
                  Retour
              </ReturnButton>
            </div>
          ) : displayableDomains.length > 0 ? (
            <OptionsContainer style={{ paddingBottom: '10px' }}>
              {displayableDomains.map(domain => ( // domain is a full formacode object
                <OptionButton
                  key={domain.code} // Use specific, unique formacode.code
                  $isSelected={selectedFavoriteDomains.includes(domain.code)}
                  onClick={() => toggleFavoriteDomainSelection(domain.code)}
                  active={activeOption === domain.code}
                  onMouseDown={() => handleOptionPressStart(domain.code)}
                  onMouseUp={handleOptionPressEnd}
                  onMouseLeave={handleOptionPressEnd}
                  onTouchStart={() => handleOptionPressStart(domain.code)}
                  onTouchEnd={handleOptionPressEnd}
                  disabled={isTyping || (!selectedFavoriteDomains.includes(domain.code) && !canSelectMoreFavorites)}
                  title={(!selectedFavoriteDomains.includes(domain.code) && !canSelectMoreFavorites) ? `Maximum ${MAX_FAVORITES} domaines favoris` : ""}
                >
                  <OptionIcon 
                    src={domain.icon || defaultDomainIcon} // domain.icon was set when creating displayableDomains
                    alt=""
                    $isSelected={selectedFavoriteDomains.includes(domain.code)}
                  />
                  {domain.descriptorRich} {/* Display name of the specific formacode */}
                </OptionButton>
              ))}
            </OptionsContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: theme.colors.textLighter }}>
              Aucun domaine n'a été sélectionné à l'étape précédente.
              <ReturnButton onClick={handleBack} style={{ margin: '20px auto', display: 'block' }}>
                Sélectionner des domaines
              </ReturnButton>
            </div>
          )}
        </OptionsScrollContainer>

        <StyledSubmitButton
          type="button"
          disabled={isSubmitDisabled}
          style={{
            opacity: isSubmitDisabled ? 0.5 : 1,
            cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
          }}
          active={submitActive}
          onMouseDown={handleSubmitPressStart}
          onMouseUp={handleSubmitPressEnd}
          onMouseLeave={() => setSubmitActive(false)}
          onTouchStart={handleSubmitPressStart}
          onTouchEnd={handleSubmitPressEnd}
        >
          {/* Show "Envoi..." if isLoading is true AND submit button was pressed */}
          {(isLoading && submitActive) ? 'Envoi...' : 
           selectedFavoriteDomains.length === 0 ? 'Sélectionner au moins 1 domaine' :
           `Continuer (${selectedFavoriteDomains.length})`}
        </StyledSubmitButton>
      </ContentWrapper>
    </Container>
  );
};

export default DomainSelectionPrincipaux;